/**
 * MessagingLayout - Responsive two-panel chat layout
 * Desktop: sidebar (conversations list) + main chat area side-by-side
 * Mobile: main chat area full-screen with slide-in sidebar overlay
 *   - Back button in header toggles the conversation sidebar overlay
 *   - Conversations always accessible without leaving the chat
 */
import React from 'react';

const MOBILE_BOTTOM_NAV_HEIGHT = 72;

export default function MessagingLayout({
  sidebarOpen,
  onToggleSidebar,
  sidebarContent,
  headerContent,
  mainContent,
  inputContent,
  hasChatSelected,
}) {
  const [mobileViewportHeight, setMobileViewportHeight] = React.useState(null);

  React.useEffect(() => {
    const updateViewportHeight = () => {
      const isMobileViewport = window.matchMedia('(max-width: 1023px)').matches;
      if (!isMobileViewport) {
        setMobileViewportHeight(null);
        return;
      }
      const vv = window.visualViewport;
      const rawHeight = vv?.height || window.innerHeight;
      const next = Math.max(360, Math.round(rawHeight - MOBILE_BOTTOM_NAV_HEIGHT));
      setMobileViewportHeight(next);
    };
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener('scroll', updateViewportHeight);
    };
  }, []);

  const closeSidebar = () => onToggleSidebar?.();

  return (
    <div
      className="flex h-[calc(100vh-64px)] min-h-[calc(100dvh-64px)] lg:h-[calc(100vh-64px)] bg-gray-50 dark:bg-neutral-950 overflow-hidden"
      style={mobileViewportHeight ? { height: `${mobileViewportHeight}px` } : undefined}
    >
      {/* Desktop Sidebar - always visible alongside chat */}
      <div
        className={`${
          hasChatSelected ? 'hidden lg:flex' : 'flex'
        } w-full lg:w-[340px] xl:w-[380px] flex-col border-r border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden flex-shrink-0`}
      >
        {sidebarContent}
      </div>

      {/* Mobile Sidebar Overlay - slide-in from left, backdrop dismissible */}
      {hasChatSelected && sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeSidebar}
            aria-hidden="true"
          />
          <div className="relative w-[85vw] max-w-[340px] h-full flex flex-col bg-white dark:bg-neutral-900 shadow-xl overflow-hidden">
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div
        className={`${
          hasChatSelected ? 'flex' : 'hidden lg:flex'
        } flex-1 flex-col bg-gray-50 dark:bg-neutral-950 overflow-hidden`}
      >
        {/* Header with context (Product/User info) - includes back/toggle button */}
        {headerContent && (
          <div className="flex-shrink-0 border-b border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
            {headerContent}
          </div>
        )}

        {/* Chat Messages Area */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {mainContent}
        </div>

        {/* Message Input - fixed at bottom */}
        {inputContent && (
          <div className="flex-shrink-0 border-t border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900">
            {inputContent}
          </div>
        )}
      </div>
    </div>
  );
}